import { useState, useEffect, useCallback } from 'react';
import CVDownload from './CVDownload';
import ExperienceLayout from './ExperienceLayout'; // Import the shared layout
import { useApiOperations } from '@utils/api-functions';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { debug } from '@utils/debug'; // Import debug utility for rule #51 compliance
 
const ExperienceManager = () => {
  const { accounts, inProgress } = useMsal();
  const isAuthenticated = accounts && accounts.length > 0;
  const [cvData, setCvData] = useState({
    personalStatement: '',
    education: [],
    roles: [],
    projects: [],
    skills: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const { getRoles, getProjects, getSkills, updateRole, deleteRole, getProfileStatus, updateProfile } = useApiOperations();

  const fetchData = useCallback(async () => {
    if (!isAuthenticated || inProgress !== InteractionStatus.None) { // Check MSAL status
       return;
    }
    setIsLoading(true);
    setFetchError(null);
    try {
      // Fetch data with individual error handling per rule #22
      const [profileResult, rolesResult, projectsResult, skillsResult] = await Promise.allSettled([
        getProfileStatus(),
        getRoles(),
        getProjects(),
        getSkills()
      ]);

      const profile = profileResult.status === 'fulfilled' ? profileResult.value : {};
      const roles = rolesResult.status === 'fulfilled' ? rolesResult.value : [];
      const projects = projectsResult.status === 'fulfilled' ? projectsResult.value : [];
      const skills = skillsResult.status === 'fulfilled' ? skillsResult.value : [];

      // Log errors if any promise was rejected using debug utility (rule #51)
      if (profileResult.status === 'rejected') {
        debug('experience:error', 'Error fetching profile:', profileResult.reason);
      }
      if (rolesResult.status === 'rejected') {
        debug('experience:error', 'Error fetching roles:', rolesResult.reason);
      }
      if (projectsResult.status === 'rejected') {
        debug('experience:error', 'Error fetching projects:', projectsResult.reason);
      }
      if (skillsResult.status === 'rejected') {
        debug('experience:error', 'Error fetching skills:', skillsResult.reason);
      }

      // Set a general fetch error if any request failed, but still process successful ones
      if ([profileResult, rolesResult, projectsResult, skillsResult].some(r => r.status === 'rejected')) {
        setFetchError('Failed to load some CV data. Displaying available information.');
      }

      setCvData({
        personalStatement: profile.personalStatement || '',
        education: profile.education || [],
        roles: (roles || []).map(role => ({
          ...role,
          startDate: role.startDate?.substring(0, 7),
          endDate: role.endDate?.substring(0, 7)
        })),
        projects: projects || [],
        skills: (skills || []).map(s => s.name)
      });
    } catch (err) {
      debug('experience:error', 'Error fetching CV data in ExperienceManager:', err);
      setFetchError('Failed to load CV data. Please try refreshing.');
      // Set empty data on error to avoid breaking UI
      setCvData({ roles: [], projects: [], skills: [] });
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, inProgress, getRoles, getProjects, getSkills]); // Use inProgress

  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None) { // Check MSAL status
      fetchData();
    } else if (!isAuthenticated && inProgress === InteractionStatus.None) { // Check MSAL status
      // Clear data if user logs out
      setCvData({ roles: [], projects: [], skills: [] });
      setIsLoading(false);
      setFetchError(null);
    }
  }, [isAuthenticated, inProgress, fetchData]); // Use inProgress

  // Handler for saving personal statement
  const handleSavePersonalStatement = async (newStatement) => {
    try {
      setIsSaving(true);
      await updateProfile({ personalStatement: newStatement });
      setSuccessMessage("Personal statement updated successfully");
      await fetchData(); // Refresh data
    } catch (error) {
      debug('experience:error', 'Error updating personal statement:', error);
      setFetchError(`Failed to update personal statement: ${error.message}`);
    } finally {
      setIsSaving(false);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  // Handler for editing a role
  const handleEditRole = async (index, updatedRoleData) => {
      const roleId = updatedRoleData._id;
      if (!roleId) {
          debug('experience:error', "Cannot update role: Missing ID.");
          setFetchError("Failed to update role: Missing ID.");
          return;
      }
      
      try {
          setIsSaving(true);
          const updatePayload = { ...updatedRoleData };
          delete updatePayload._id;
          
          await updateRole(roleId, updatePayload);
          setSuccessMessage("Role updated successfully");
          await fetchData();
      } catch (error) {
          debug('experience:error', 'Error updating role:', error);
          setFetchError(`Failed to update role: ${error.message}`);
      } finally {
          setIsSaving(false);
          // Clear success message after 3 seconds
          setTimeout(() => setSuccessMessage(null), 3000);
      }
  };

  // Handler for deleting a role
  const handleDeleteRole = async (indexToDelete) => {
      const roleToDelete = cvData.roles[indexToDelete];
      if (!roleToDelete || !roleToDelete._id) {
          debug('experience:error', "Cannot delete role: Missing role data or ID.");
          setFetchError("Failed to delete role: Missing ID.");
          return;
      }
      
      try {
          setIsSaving(true);
          await deleteRole(roleToDelete._id);
          setSuccessMessage("Role deleted successfully");
          await fetchData();
      } catch (error) {
          debug('experience:error', 'Error deleting role:', error);
          setFetchError(`Failed to delete role: ${error.message}`);
      } finally {
          setIsSaving(false);
          // Clear success message after 3 seconds
          setTimeout(() => setSuccessMessage(null), 3000);
      }
  };

  // Handler for saving the entire profile
  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);
      // This would typically save all profile data, but we're just showing a success message
      setSuccessMessage("Profile saved successfully");
    } catch (error) {
      debug('experience:error', 'Error saving profile:', error);
      setFetchError(`Failed to save profile: ${error.message}`);
    } finally {
      setIsSaving(false);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  if (isLoading || inProgress !== InteractionStatus.None) {
     return (
      <div className="flex justify-center items-center h-72">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-huntd-info"></div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="p-4 bg-huntd-error/10 text-huntd-error rounded">
        {fetchError}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">CV Management</h2>
          <div className="flex gap-4">
            <CVDownload cvData={cvData} />
          </div>
        </div>
        
        {/* Use the shared ExperienceLayout component */}
        <ExperienceLayout
          personalStatement={cvData.personalStatement}
          education={cvData.education}
          roles={cvData.roles}
          skills={cvData.skills}
          projects={cvData.projects}
          validatedRoles={new Array(cvData.roles.length).fill(true)} // Add validatedRoles prop
          onSavePersonalStatement={handleSavePersonalStatement}
          onEditRole={handleEditRole}
          onDeleteRole={handleDeleteRole}
          saveProfile={handleSaveProfile}
          saveErrors={fetchError ? [fetchError] : []}
          successMessage={successMessage}
          isSaving={isSaving}
          showSaveButton={false} // Hide save button in this view
        />
      </div>
    </div>
  );
};

export default ExperienceManager;
